import { FC, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { SiteFooter } from "../../components/footer/SiteFooter";
import { MarketingHeader } from "./MarketingHeader";
import "./LycheeHub.scss";

/**
 * Lychee Hub - 面向泛内容创作者的一站式 AI 创作平台宣传页
 * 根据 doc/Lychee Hub宣传页.html 实现
 */
export const LycheeHub: FC = () => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Lychee Hub - 一个灵感，长成多种内容 | Lychee Voice";
    
    // 初始化 fade-up 动画
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    document.querySelectorAll(".fade-up").forEach((element, index) => {
      (element as HTMLElement).style.transitionDelay = `${Math.min(index % 4, 3) * 70}ms`;
      observer.observe(element);
    });

    // 初始化音频播放
    document.querySelectorAll<HTMLButtonElement>("button.play").forEach((button) => {
      const audioTarget = button.dataset.audioTarget;
      const audio = audioTarget ? document.getElementById(audioTarget) as HTMLAudioElement | null : null;
      const audioWave = audio
        ? button.closest(".hub-mini-player")?.querySelector<HTMLElement>("[data-audio-wave]")
        : null;
      const audioLabel = button.dataset.audioLabel || "试听";

      if (audio) {
        audio.addEventListener("timeupdate", () => {
          if (
            !audioWave ||
            !Number.isFinite(audio.duration) ||
            audio.duration <= 0
          )
            return;
          const progress = Math.min(
            100,
            Math.max(0, (audio.currentTime / audio.duration) * 100)
          );
          audioWave.style.setProperty("--audio-progress", `${progress}%`);
        });

        audio.addEventListener("ended", () => {
          button.textContent = "▶";
          button.setAttribute("aria-label", `播放${audioLabel}`);
          audioWave?.classList.remove("is-playing");
          audioWave?.style.setProperty("--audio-progress", "0%");
        });
      }

      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (audio) {
          if (!audio.paused) {
            audio.pause();
            button.textContent = "▶";
            button.setAttribute("aria-label", `播放${audioLabel}`);
            audioWave?.classList.remove("is-playing");
            return;
          }

          // 暂停其他音频
          document.querySelectorAll<HTMLAudioElement>("audio[data-hub-audio]").forEach((otherAudio) => {
            if (otherAudio === audio) return;
            otherAudio.pause();
            const otherButton = document.querySelector<HTMLButtonElement>(
              `[data-audio-target="${otherAudio.id}"]`
            );
            const otherLabel = otherButton?.dataset.audioLabel || "试听";
            if (otherButton) otherButton.textContent = "▶";
            otherButton?.setAttribute("aria-label", `播放${otherLabel}`);
            otherButton
              ?.closest(".hub-mini-player")
              ?.querySelector("[data-audio-wave]")
              ?.classList.remove("is-playing");
          });

          audio.play().then(() => {
            button.textContent = "Ⅱ";
            button.setAttribute("aria-label", `暂停${audioLabel}`);
            audioWave?.classList.add("is-playing");
          }).catch(() => {
            button.textContent = "▶";
            button.setAttribute("aria-label", `播放${audioLabel}`);
            audioWave?.classList.remove("is-playing");
          });
          return;
        }
      });
    });

    // Hub 入口跳转逻辑
    const handleHubTarget = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const link = target.closest<HTMLAnchorElement>("[data-hub-target]");
      const startLink = target.closest<HTMLAnchorElement>("[data-hub-start]");

      if (link) {
        event.preventDefault();
        const href = link.getAttribute("href");
        if (href) navigate(href.replace(".html", ""));
      }

      if (startLink) {
        event.preventDefault();
        const targetPage = startLink.dataset.hubStart;
        if (targetPage) navigate(targetPage.replace(".html", ""));
      }
    };

    document.addEventListener("click", handleHubTarget);

    // 处理 details 展开
    document.querySelectorAll<HTMLDetailsElement>("details.hub-entry[open]").forEach((entry) =>
      entry.removeAttribute("open")
    );
    document.querySelectorAll("details.hub-entry").forEach((entry) => {
      entry.addEventListener("toggle", () => {
        const detailsEl = entry as HTMLDetailsElement;
        if (!detailsEl.open) return;
        document
          .querySelectorAll<HTMLDetailsElement>("details.hub-entry[open]")
          .forEach((other) => {
            if (other !== entry) other.removeAttribute("open");
          });
      });
    });

    document.addEventListener("click", (event) => {
      const target = event.target as HTMLElement;
      if (target.closest("details.hub-entry")) return;
      document
        .querySelectorAll<HTMLDetailsElement>("details.hub-entry[open]")
        .forEach((entry) => entry.removeAttribute("open"));
    });

    return () => {
      observer.disconnect();
      document.removeEventListener("click", handleHubTarget);
    };
  }, [navigate]);

  return (
    <div className="lychee-hub-page" ref={rootRef}>
      {/* 顶部导航 - 复用现有组件 */}
      <MarketingHeader />

      <main className="hub-refresh-page">
        {/* Hero 区域 */}
        <section className="hub-refresh-hero">
          <div className="container hub-refresh-hero-grid">
            <div className="hub-refresh-copy fade-up">
              <p className="hub-refresh-eyebrow">
                LYCHEE HUB · GENERATIVE CONTENT STUDIO
              </p>
              <h1>
                一个灵感，
                <br />
                长成多种内容
              </h1>
              <p>
                面向泛内容创作者的一站式 AI 创作平台。按需选择图像、声音、播客或角色声音工具，
                让想法更快变成可看、可听、可分享的内容。
              </p>
              <a
                className="hub-refresh-primary"
                href="/hub/ai-voice"
                data-hub-start="/hub/ai-voice"
              >
                开始创作
              </a>
            </div>

            <figure className="hub-refresh-visual fade-up">
              <img
                src="/lychee-hub/lychee-hub-hero.png"
                alt="由图像、音频、播客和角色声音组成的多内容创作场景"
              />
            </figure>
          </div>

          {/* 创作入口条 */}
          <div
            className="container hub-entry-strip fade-up"
            aria-label="Lychee Hub 创作入口"
          >
            {/* 视觉入口 */}
            <div className="hub-entry visual">
              <a
                className="hub-entry-link"
                href="/hub/ai-image"
                data-hub-target
              >
                <span className="hub-entry-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <rect x="3" y="4" width="18" height="16" rx="3"></rect>
                    <path d="m6 16 4-4 3 3 2-2 3 3"></path>
                    <circle cx="16.5" cy="8.5" r="1.5"></circle>
                  </svg>
                </span>
                <span className="hub-entry-copy">
                  <strong>视觉</strong>
                  <span data-label="AI 生图">进入工具 →</span>
                </span>
              </a>
            </div>

            {/* 声音入口 */}
            <details className="hub-entry audio">
              <summary>
                <span className="hub-entry-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d="M4 12h2m2-5v10m3-13v16m3-12v8m3-11v14m3-9v4"></path>
                  </svg>
                </span>
                <span className="hub-entry-copy">
                  <strong>声音</strong>
                  <span data-label="音频与配音">选择工具 +</span>
                </span>
              </summary>
              <div className="hub-entry-menu">
                <a href="/hub/ai-voice" data-hub-target>
                  <span>AI 音频</span>
                  <span>→</span>
                </a>
                <a href="/hub/text-to-audio" data-hub-target>
                  <span>文本转语音</span>
                  <span>→</span>
                </a>
              </div>
            </details>

            {/* 对谈入口 */}
            <div className="hub-entry podcast">
              <a
                className="hub-entry-link"
                href="/hub/podcast"
                data-hub-target
              >
                <span className="hub-entry-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <rect x="8" y="3" width="8" height="13" rx="4"></rect>
                    <path d="M5 11a7 7 0 0 0 14 0M12 18v3M9 21h6"></path>
                  </svg>
                </span>
                <span className="hub-entry-copy">
                  <strong>对谈</strong>
                  <span data-label="AI 播客">进入工具 →</span>
                </span>
              </a>
            </div>

            {/* 角色入口 */}
            <details className="hub-entry voice">
              <summary>
                <span className="hub-entry-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d="M12 2v20M2 12h20M5 5l14 14M19 5 5 19"></path>
                  </svg>
                </span>
                <span className="hub-entry-copy">
                  <strong>角色</strong>
                  <span data-label="音色资产">选择工具 +</span>
                </span>
              </summary>
              <div className="hub-entry-menu">
                <a href="/hub/voice-library/public" data-hub-target>
                  <span>音色库</span>
                  <span>→</span>
                </a>
                <a href="/hub/voice-clone" data-hub-target>
                  <span>音色克隆</span>
                  <span>→</span>
                </a>
                <a href="/hub/voice-design" data-hub-target>
                  <span>音色设计</span>
                  <span>→</span>
                </a>
              </div>
            </details>
          </div>
        </section>

        {/* 展示区域 */}
        <section className="hub-showcase">
          <div className="container">
            <div className="hub-showcase-heading fade-up">
              <p className="hub-refresh-section-eyebrow">
                ONE IDEA, MANY OUTPUTS
              </p>
              <h2>同一个灵感，可以有不同表达</h2>
              <p>选择一种合适的内容形态，把灵感发展成更完整的表达。</p>
            </div>

            <div className="hub-output-shell fade-up">
              <div className="hub-inspiration-note">
                <span>示例灵感</span>
                <strong>一名生活在未来海底城市的少女</strong>
              </div>

              <div className="hub-output-grid">
                {/* 01 角色概念图 */}
                <article className="hub-output-card">
                  <h3>01 角色概念图</h3>
                  <p>建立人物与视觉世界</p>
                  <img
                    src="/lychee-hub/underwater-girl-concept.png"
                    alt="一位生活在未来海底城市的少女角色概念图"
                  />
                </article>

                {/* 02 音色设计 */}
                <article className="hub-output-card">
                  <h3>02 音色设计</h3>
                  <p>从角色设定生成可复用声线</p>
                  <div className="hub-result-panel amber">
                    <span className="hub-design-label">生成提示词</span>
                    <p className="hub-voice-prompt">
                      清澈冷静的少女声线，带轻微气声，情绪克制而好奇。
                    </p>
                    <div className="hub-mini-player">
                      <button
                        className="play solid"
                        type="button"
                        aria-label="播放音色试听"
                        data-audio-target="underwaterGirlVoicePreview"
                        data-audio-label="音色试听"
                      >
                        ▶
                      </button>
                      <span
                        className="hub-audio-wave"
                        data-audio-wave
                        aria-hidden="true"
                      >
                        <img
                          className="wave-base"
                          src="/lychee-hub/underwater-girl-voice-preview-waveform.png"
                          alt=""
                        />
                        <img
                          className="wave-progress"
                          src="/lychee-hub/underwater-girl-voice-preview-waveform.png"
                          alt=""
                        />
                      </span>
                      <span className="hub-audio-time">00:07</span>
                    </div>
                    <audio
                      id="underwaterGirlVoicePreview"
                      data-hub-audio
                      preload="metadata"
                      src="/lychee-hub/underwater-girl-voice-preview.mp3"
                    />
                  </div>
                </article>

                {/* 03 角色独白 */}
                <article className="hub-output-card">
                  <h3>03 角色独白</h3>
                  <p>让文字拥有角色声音</p>
                  <div className="hub-result-panel purple">
                    <span className="hub-script-label">音频脚本</span>
                    <p className="hub-result-quote">
                      今天是在海底城的第三百六十二天。这里模拟的阳光落进水里散开的光斑，
                      比小时候在陆地上见过的还要好看。
                    </p>
                    <div className="hub-mini-player">
                      <button
                        className="play solid"
                        type="button"
                        aria-label="播放角色独白"
                        data-audio-target="underwaterGirlMonologue"
                        data-audio-label="角色独白"
                      >
                        ▶
                      </button>
                      <span
                        className="hub-audio-wave"
                        data-audio-wave
                        aria-hidden="true"
                      >
                        <img
                          className="wave-base"
                          src="/lychee-hub/underwater-girl-monologue-waveform.png"
                          alt=""
                        />
                        <img
                          className="wave-progress"
                          src="/lychee-hub/underwater-girl-monologue-waveform.png"
                          alt=""
                        />
                      </span>
                      <span className="hub-audio-time">00:43</span>
                    </div>
                    <audio
                      id="underwaterGirlMonologue"
                      data-hub-audio
                      preload="metadata"
                      src="/lychee-hub/underwater-girl-monologue.mp3"
                    />
                  </div>
                </article>

                {/* 04 世界观播客 */}
                <article className="hub-output-card">
                  <h3>04 世界观播客</h3>
                  <p>用对谈展开设定</p>
                  <div className="hub-result-panel coral">
                    <span className="hub-episode-label">双人播客 · EP 01</span>
                    <strong className="hub-episode-title">
                      如果人类住进海底：未来生活全景
                    </strong>
                    <p className="hub-podcast-summary">
                      从居住、交通、食物到能源与安全，畅想人类迁入海底后的日常生活。
                    </p>
                    <span className="hub-podcast-speakers">小何 × 云舟</span>
                    <div className="hub-mini-player">
                      <button
                        className="play solid"
                        type="button"
                        aria-label="播放双人播客"
                        data-audio-target="underwaterCityPodcast"
                        data-audio-label="双人播客"
                      >
                        ▶
                      </button>
                      <span
                        className="hub-audio-wave"
                        data-audio-wave
                        aria-hidden="true"
                      >
                        <img
                          className="wave-base"
                          src="/lychee-hub/underwater-city-podcast-waveform.png"
                          alt=""
                        />
                        <img
                          className="wave-progress"
                          src="/lychee-hub/underwater-city-podcast-waveform.png"
                          alt=""
                        />
                      </span>
                      <span className="hub-audio-time">04:52</span>
                    </div>
                    <audio
                      id="underwaterCityPodcast"
                      data-hub-audio
                      preload="metadata"
                      src="/lychee-hub/underwater-city-podcast.mp3"
                    />
                  </div>
                </article>
              </div>
            </div>
          </div>
        </section>

        {/* CTA 区域 */}
        <section className="hub-refresh-cta">
          <div className="container hub-refresh-cta-inner fade-up">
            <div>
              <h2>让一个灵感，真正长成作品</h2>
              <p>选择适合当前目标的工具，立即开始创作。</p>
            </div>
            <a
              className="button"
              href="/hub/ai-voice"
              data-hub-start="/hub/ai-voice"
            >
              立即开始创作 →
            </a>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};
